// @flow
import { createSelector } from 'reselect';

// Selectors
import { getAppointment } from 'store/reducers/appointment/selectors';
import { isLicenseNumberValid } from 'store/reducers/dealerSettings/selectors';

// Constants
import { APP_FLOW } from 'constants/appFlow';
import { C8_TOGGLE_PRIVACY_SETTINGS_VALUES } from 'constants/privacySettings';

const { FIND_ME } = APP_FLOW;

// TODO: update the shape of userInteractions
export const getUserInteractions = (state: any) => state.userInteractions;

export const isPrivateRoute = createSelector(
  getUserInteractions,
  ({ privateRoute }) => privateRoute
);

export const isLoadingAppointment = createSelector(
  [getUserInteractions],
  userInteractions => userInteractions.isLoadingAppointment
);

export const getAppFlow = createSelector(
  getUserInteractions,
  ({ appFlow }) => appFlow
);

export const getAppName = createSelector(
  getUserInteractions,
  ({ appName }) => appName
);

// Page Vehicle
export const getPageVehicleState = createSelector(
  getUserInteractions,
  ({ pageVehicle }) => pageVehicle
);

export const getNoFindsAlertShown = createSelector(
  getPageVehicleState,
  ({ noFindsAlertShown }) => noFindsAlertShown
);

export const getShowMobileFooter = createSelector(
  getPageVehicleState,
  ({ showMobileFooter }) => showMobileFooter
);

export const getVehiclePageRevisit = createSelector(
  getPageVehicleState,
  ({ vehiclePageRevisit }) => vehiclePageRevisit
);

export const getIdkSelected = createSelector(
  getPageVehicleState,
  ({ idkSelected }) => idkSelected
);

export const getTrimsSelectedPosition = createSelector(
  getPageVehicleState,
  ({ trimsSelectedPosition }) => trimsSelectedPosition
);

// Page Appointment
export const getPageAppointmentState = createSelector(
  getUserInteractions,
  ({ pageAppointment }) => pageAppointment
);

export const getFilterDaysBy = createSelector(
  getPageAppointmentState,
  ({ filterDaysBy }) => filterDaysBy
);

export const getSelectedDay = createSelector(
  getPageAppointmentState,
  ({ selectedDay, filterDaysBy }) => {
    if (filterDaysBy && selectedDay) {
      const exist = selectedDay.timeslots.find(
        ({ time }) =>
          filterDaysBy === 'morning' ? time < '12:00:00' : time >= '12:00:00'
      );
      if (!exist) {
        return null;
      }
    }
    return selectedDay;
  }
);

export const getGtmFirstAvailableTimeslot = createSelector(
  getPageAppointmentState,
  ({ gtmFirstAvailableTimeslot }) => gtmFirstAvailableTimeslot
);

export const isModifyEnabled = createSelector(
  getUserInteractions,
  ({ modifyEnabled }) => modifyEnabled
);

export const isApptModified = createSelector(
  getUserInteractions,
  ({ apptModified }) => apptModified
);

export const isPopulateMobilePhoneNumber = createSelector(
  getUserInteractions,
  ({ populateMobilePhoneNumber }) => populateMobilePhoneNumber
);

export const wasStartEventSent = createSelector(
  getUserInteractions,
  ({ startEventSent }) => startEventSent
);

export const getNoLongerAvailableDate = createSelector(
  getPageAppointmentState,
  ({ noLongerAvailableDate }) => noLongerAvailableDate
);

export const getLastTimeslotSelected = createSelector(
  getPageAppointmentState,
  ({ lastTimeslotSelected }) => lastTimeslotSelected
);

export const getSelectedTimeslotInPromoRange = createSelector(
  getPageAppointmentState,
  ({ selectedTimeslotInPromoRange }) => selectedTimeslotInPromoRange
);
export const getDateApptPickerTouchByUser = createSelector(
  getPageAppointmentState,
  ({ dateApptPickerTouchByUser }) => dateApptPickerTouchByUser
);

export const getPageReviewState = createSelector(
  getUserInteractions,
  ({ pageReview }) => pageReview
);

export const getContactInfoMissing = createSelector(
  getPageReviewState,
  ({ contactInfoMissing }) => contactInfoMissing
);

export const isContactInfoFormValid = createSelector(
  getUserInteractions,
  ({ contactInfoFormValid }) => contactInfoFormValid
);

export const isModifyEnabledFindMe = createSelector(
  getUserInteractions,
  ({ modifyEnabled, appFlow }) => {
    return modifyEnabled && appFlow === FIND_ME;
  }
);

export const getSearchCriteriaFindMe = createSelector(
  getUserInteractions,
  ({ searchCriteriaFindMe }) => {
    return searchCriteriaFindMe;
  }
);

export const getSelfCheckInEnabled = createSelector(
  getUserInteractions,
  ({ selfCheckinEnabled }) => {
    return selfCheckinEnabled;
  }
);

export const getSelfCheckInExtendedEnabled = createSelector(
  getUserInteractions,
  ({ selfCheckinExtendedEnabled }) => {
    return selfCheckinExtendedEnabled;
  }
);

export const getModelLabelOther = createSelector(
  getUserInteractions,
  ({ modelLabelOther }) => {
    return modelLabelOther;
  }
);

export const getMakeLabelOther = createSelector(
  getUserInteractions,
  ({ makeLabelOther }) => {
    return makeLabelOther;
  }
);

// QuickAppointment
export const getQuickAppointmentState = createSelector(
  getUserInteractions,
  ({ quickAppointment }) => quickAppointment
);

export const getQuickApptEnabled = createSelector(
  getQuickAppointmentState,
  ({ isQuickApptEnabled }) => {
    return isQuickApptEnabled;
  }
);

export const getLocalizedQuickApptService = createSelector(
  getQuickAppointmentState,
  ({ localizedQuickApptService }) => {
    return localizedQuickApptService;
  }
);

export const getGUID = createSelector(getUserInteractions, ({ guid }) => guid);

export const getSavedMobile = createSelector(
  getUserInteractions,
  ({ savedMobile }) => savedMobile || null
);

export const getRecaptchaToken = createSelector(
  getUserInteractions,
  ({ recaptchaToken }) => recaptchaToken || null
);

// The selector isBookingEnabled was moved here in order to prevent a
// circular dependence between dealerSettings and appointment selectors.

export const isBookingEnabled = createSelector(
  getAppointment,
  isContactInfoFormValid,
  (
    {
      vehicle,
      services,
      declinedServices,
      servicePoint,
      contactInfo,
      startTime,
      privacySettings: { textPrivacySetting }
    },
    isContactInfoFormValid
  ) => {
    const auxContactInfo = { ...contactInfo };
    if (
      textPrivacySetting === C8_TOGGLE_PRIVACY_SETTINGS_VALUES.TEXT_DISABLED
    ) {
      delete auxContactInfo.mobilePhone;
    }
    const contactInfoHasRequiredFields = Object.keys(auxContactInfo)
      .filter(key => key !== 'obfuscatedBy' && key !== 'extId')
      .map(key => Boolean(contactInfo[key]))
      .reduce((a, b) => a && b, true);
    const contactInfoValid =
      isContactInfoFormValid && contactInfoHasRequiredFields;
    const servicesSelected =
      services.length +
        declinedServices.length +
        (servicePoint ? Object.keys(servicePoint).length : 0) >
      0;
    const vehicleSelected = Object.keys(vehicle).length > 0;
    return (
      vehicleSelected &&
      servicesSelected &&
      contactInfoValid &&
      isLicenseNumberValid &&
      Boolean(startTime)
    );
  }
);



// WEBPACK FOOTER //
// ./src/store/reducers/userInteractions/selectors.js